import { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * @module useQuestionnaireDetails
 * @description Custom hook for fetching detailed information about a specific questionnaire.
 * Automatically fetches data when the questionnaire ID changes and manages loading and error states.
 *
 * @requires react
 * @requires ../services/api
 */

/**
 * Hook to fetch and manage questionnaire details
 * @function useQuestionnaireDetails
 * @param {string|number|null} questionnaireId - The ID of the questionnaire to fetch
 * @returns {Object} Object containing questionnaire data, loading state, and error state
 */
export const useQuestionnaireDetails = (questionnaireId) => {
  /**
   * @state {Object|null} questionnaire - The fetched questionnaire data or null if not loaded
   */
  const [questionnaire, setQuestionnaire] = useState(null);

  /**
   * @state {boolean} loading - Indicates if the API request is in progress
   */
  const [loading, setLoading] = useState(false);

  /**
   * @state {string} error - Error message if the API request fails, empty string otherwise
   */
  const [error, setError] = useState('');

  /**
   * Effect hook to fetch questionnaire details when the ID changes
   * @effect
   * @param {string|number|null} questionnaireId - The ID of the questionnaire to fetch
   */
  useEffect(() => {
    /**
     * Fetches questionnaire details from the API
     * @async
     * @function fetchQuestionnaire
     * @returns {Promise<void>}
     */
    const fetchQuestionnaire = async () => {
      if (!questionnaireId) return;

      setLoading(true);
      setError('');

      try {
        const { data } = await api.get(`questionnaire/${questionnaireId}`);
        setQuestionnaire({
          ...data.questionnaire,
          questions: data.questions,
        });
      } catch (err) {
        setError('Erro ao buscar os detalhes do questionário');
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaire();
  }, [questionnaireId]);

  /**
   * @typedef {Object} UseQuestionnaireDetailsReturn
   * @property {Object|null} questionnaire - The fetched questionnaire data or null if not loaded
   * @property {boolean} loading - Indicates if the API request is in progress
   * @property {string} error - Error message if the API request fails, empty string otherwise
   */

  /**
   * @returns {UseQuestionnaireDetailsReturn} The hook's return values
   */
  return { questionnaire, loading, error };
};
